// Human-readable description of the import CSV. Column names and limits mirror the backend
// `PublisherWebsiteImportColumns` / `PublisherWebsiteSettingsRules`; the value lists are built
// from the generated enums so a new vertical or type shows up here automatically. The
// per-niche `price_<niche>` columns stay out of this reference on purpose: the downloadable
// template already contains them (RES-7302).

import * as common_pb from 'respona_api/generated/common_pb';
import { webSearchLanguages } from '@constants/languages.contstants';
import { orderItemVerticalToString } from '@utils/verticals';
import { websiteTypeToString } from '@utils/websiteTypes';

export type ImportReferenceValue = {
  /** What to type into the cell. */
  code: string;
  label: string;
};

export type ImportColumnRule = {
  column: string;
  rule: string;
};

const UNDEFINED_VERTICAL_NAME = 'UNDEFINED_VERTICAL';

export const IMPORT_COLUMN_RULES: ImportColumnRule[] = [
  {
    column: 'domain',
    rule: 'Required. Domain or URL, for example site.io or https://www.site.io/',
  },
  {
    column: 'guest_post_price',
    rule: 'Whole number from 1 to 9999 (USD). Empty: our suggested price',
  },
  { column: 'guest_post_link_limit', rule: 'Links per guest post, from 1 to 5' },
  { column: 'link_insertion_enabled', rule: 'true / false (also yes / no, 1 / 0)' },
  { column: 'link_insertion_price', rule: 'From 1 to 9999, used when link insertion is enabled' },
  { column: 'turnaround_days', rule: 'From 1 to 10' },
  { column: 'article_words_min', rule: 'From 400 to 10000, lower than article_words_max' },
  { column: 'article_words_max', rule: 'From 800 to 10000' },
  { column: 'languages', rule: 'Up to 10 ISO 639-1 codes from the list below, no duplicates' },
  { column: 'verticals', rule: 'Values from the list below' },
  { column: 'website_types', rule: 'Values from the list below' },
  { column: 'notes', rule: 'Free text' },
];

export const IMPORT_MULTI_VALUE_HINT =
  'Separate multiple values with ";" (comma and "|" work too). Column names are case-insensitive. Empty cells keep the values we suggest for the website.';

const uniqueByCode = (values: ImportReferenceValue[]): ImportReferenceValue[] => {
  const seen = new Set<string>();

  return values.filter(({ code }) => {
    if (seen.has(code)) {
      return false;
    }
    seen.add(code);
    return true;
  });
};

/** `lang_zh-CN` in the search list is the ISO 639-1 code `zh` for the import. */
export const getImportLanguages = (): ImportReferenceValue[] =>
  uniqueByCode(
    webSearchLanguages
      .filter(({ value }) => value.length > 0)
      .map(({ value, label }) => ({
        code: value
          .replace(/^lang_/, '')
          .split('-')[0]
          .toLowerCase(),
        label,
      }))
  );

export const getImportVerticals = (): ImportReferenceValue[] =>
  Object.entries(common_pb.OrderItemVertical)
    .filter(([name]) => name !== UNDEFINED_VERTICAL_NAME)
    .map(([name, value]) => ({
      code: name,
      label: orderItemVerticalToString(
        value as common_pb.OrderItemVerticalMap[keyof common_pb.OrderItemVerticalMap]
      ),
    }));

export const getImportWebsiteTypes = (): ImportReferenceValue[] =>
  Object.entries(common_pb.PublisherWebsiteType).map(([name, value]) => ({
    code: name,
    label: websiteTypeToString(
      value as common_pb.PublisherWebsiteTypeMap[keyof common_pb.PublisherWebsiteTypeMap]
    ),
  }));
